import type { PlanComponent, PlanSummary } from '@rheora/enrollment-contract';

/**
 * ── NO ERCOT CODE OR RAW ENUM ON A CUSTOMER SCREEN (E-SF19 P2) ──────────────
 *
 * The storefront rendered upstream identifiers verbatim: "Delivery utility (TDU):
 * CNP", cards reading "Delivery: ONCR", and a price breakdown listing
 * `REP_ENERGY` and `TDU_PASSTHROUGH` as if those were line-item names. They are
 * wire tokens. A Texas shopper has no way to decode them, and the human name for
 * the utility was already sitting in the same funnel session, unused
 * (`GET /bff/funnel` → "CenterPoint Energy").
 *
 * This module is the ONE translation layer between the catalogue's vocabulary and
 * the customer's. It returns i18n KEYS (never copy) so the strings stay in
 * `locales/` and the components stay render-only.
 *
 * ⚠️ SCOPE. This is presentation only and touches nothing that prices, filters or
 * enrolls: `filterPlansByTdu` still matches on the raw `tdsp` CODE, and the funnel
 * still carries `tduCode`. Translating a value the customer READS must never
 * change the value the system MATCHES ON — that is how a display fix becomes a
 * wrong-utility enrollment.
 */

/**
 * The i18n key naming a plan's rate component, or `null` when the code is one
 * this build does not recognise.
 *
 * `null` rather than a humanised guess: `REP_ENERGY` → "Rep Energy" is not
 * English, and title-casing an unknown upstream token produces confident nonsense.
 * The caller SUPPRESSES an unrecognised component rather than printing its code —
 * an incomplete breakdown is honest, an unreadable one is not, and every component
 * is already accounted for in the headline average price the customer is
 * comparing on.
 */
export function planComponentLabelKey(component: PlanComponent): string | null {
  switch (component.code.trim().toUpperCase()) {
    case 'REP_ENERGY':
    case 'WHOLESALE':
      return 'shop.detail.component.repEnergy';
    case 'TDU_PASSTHROUGH':
    case 'TDU_DELIVERY':
      return 'shop.detail.component.tduDelivery';
    case 'REP_BASE':
      return 'shop.detail.component.repBase';
    case 'TDU_FIXED':
      return 'shop.detail.component.tduFixed';
    case 'ANCILLARY':
      return 'shop.detail.component.ancillary';
    case 'OPEX':
    case 'MARGIN':
      return 'shop.detail.component.repCosts';
    case 'TAXES':
      return 'shop.detail.component.taxes';
    default:
      return null;
  }
}

/** The components this build can name, in catalogue order. Unknown codes drop out. */
export function namedPlanComponents(
  components: readonly PlanComponent[],
): readonly { readonly component: PlanComponent; readonly labelKey: string }[] {
  return components.flatMap((component) => {
    const labelKey = planComponentLabelKey(component);
    return labelKey === null ? [] : [{ component, labelKey }];
  });
}

/**
 * The i18n key naming a plan's rate TYPE, so `FIXED` / `VARIABLE` / `INDEXED`
 * never render as shouted enum members on a badge.
 *
 * Unlike a component code this ALWAYS resolves: the three members are a closed
 * contract enum the shelf already filters and sorts on, and a plan whose rate type
 * we could not name is one we should not be selling. An unrecognised value falls
 * to the neutral key rather than the raw token.
 */
export function planRateTypeLabelKey(plan: PlanSummary): string {
  switch (plan.fixedOrIndexed.trim().toUpperCase()) {
    case 'FIXED':
      return 'shop.detail.rateType.fixed';
    case 'VARIABLE':
      return 'shop.detail.rateType.variable';
    case 'INDEXED':
      return 'shop.detail.rateType.indexed';
    default:
      return 'shop.detail.rateType.unknown';
  }
}
